import { useId, type ReactNode } from 'react';

export type BmoRadioOption<T extends string> = {
  label: string;
  value: T;
};

type Props<T extends string> = {
  options: BmoRadioOption<T>[];
  /** Injected by AntD Form.Item (custom control contract). */
  value?: T;
  onChange?: (value: T) => void;
  disabled?: boolean;
  name?: string;
  /** Stack the options vertically (one per line) instead of the default
   *  horizontal flex-wrap flow. */
  vertical?: boolean;
  /** Give each option a bordered full-width card. Only meaningful with
   *  `vertical` — a wrapping horizontal row of cards is the tile grid
   *  (OptionCardGroup), not this. */
  cards?: boolean;
  /**
   * Inline content rendered directly BENEATH the currently-selected option,
   * inside that option's own wrapper — the per-option "reveal slot" a
   * CheckboxGroup option gets from `revealFieldId`. Called only for the
   * selected option, so at most one slot can ever be open.
   *
   * Returning null (or omitting the prop entirely) keeps the original flat
   * DOM: one `<label class="bmo-radio">` per option as a direct flex child.
   * That matters — every existing form takes that path, and the wrapper
   * would otherwise change the `.bmo-radio-group--cards` chrome and the
   * accessibility tree for groups that have no reveals at all.
   */
  renderAfter?: (value: T) => ReactNode;
  /**
   * Accessibility wiring for the radiogroup itself (WCAG 2.1 — 1.3.1, 3.3.1,
   * 4.1.2). A `role="radiogroup"` with no accessible name announces as an
   * unlabelled group, so the question text has to be pointed at explicitly:
   * unlike a text input there is no `htmlFor` relationship to lean on.
   */
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  ariaRequired?: boolean;
  ariaInvalid?: boolean;
};

/**
 * BMO radio group — renders the design guide's exact radio (24px ring, 2px
 * border, 12px blue dot) via the .bmo-radio CSS in global.css. Works as an
 * AntD Form.Item child (reads `value`, calls `onChange`).
 */
export function BmoRadioGroup<T extends string>({
  options,
  value,
  onChange,
  disabled,
  name,
  vertical,
  cards,
  renderAfter,
  ariaLabelledBy,
  ariaDescribedBy,
  ariaRequired,
  ariaInvalid,
}: Props<T>) {
  const generated = useId();
  const groupName = name ?? generated;

  return (
    <div
      className={[
        'bmo-radio-group',
        vertical ? 'bmo-radio-group--vertical' : '',
        cards ? 'bmo-radio-group--cards' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="radiogroup"
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      aria-required={ariaRequired || undefined}
      aria-invalid={ariaInvalid || undefined}
    >
      {options.map((o) => {
        const checked = value === o.value;
        const after = checked ? (renderAfter?.(o.value) ?? null) : null;
        const control = (
          <label className={after ? 'bmo-radio bmo-radio--top' : 'bmo-radio'} key={o.value}>
            <input
              type="radio"
              name={groupName}
              value={o.value}
              checked={checked}
              disabled={disabled}
              onChange={() => onChange?.(o.value)}
            />
            <span className="bmo-radio__circle">
              <span className="bmo-radio__dot" />
            </span>
            <span className="bmo-radio__label">{o.label}</span>
          </label>
        );
        if (!after) return control;
        // Reveal present: the option becomes a flex column so the borrowed
        // field stacks UNDER its radio rather than beside it (same rationale
        // as the CheckboxGroup slot — a tall reveal must not shove the next
        // option sideways). The 26-px indent lines the input up with the
        // option label; inside a card the card padding already insets it.
        return (
          <div
            key={o.value}
            className={cards ? 'bmo-option-row-card' : undefined}
            style={{ display: 'flex', flexDirection: 'column', width: '100%' }}
          >
            {control}
            <div style={{ marginLeft: cards ? 0 : 26, marginTop: 12 }}>{after}</div>
          </div>
        );
      })}
    </div>
  );
}
